import { forwardRef, useEffect, useState } from 'react'
import { ThreeElements, useFrame, useThree } from '@react-three/fiber';
import { FunctionComponent, memo, useRef } from 'react';
import { 
  BufferGeometry, 
  Float32BufferAttribute, 
  Group, 
  // Line as _Line, 
  Object3D, 
  Object3DEventMap 
} from 'three';
import mergeRefs from '../../r3f-components/utils/general';
import { extend } from '@react-three/fiber'
import Beam from './Beam';
import { XRControllerModel } from './ControllerModel/XRControllerModel';
import { Image } from '../../r3f-components';
import { getGroundPlaneHitData, getHitData, IHitMatrixData, setHitTestSource, setLocalReferenceSpace } from './webxr-utils';
import { Jute } from '../../..';

// const Line = extend({ _Line })

interface IParentProps {
    // index?: number;
    space: 'grip' | 'target',
    showRay: boolean,
    handedness: XRHandedness,
    intersectionDetected?: boolean;
    isPlacementMode?: boolean;
    selectActive?: boolean;
    transformActive?: boolean;
    hitTestingActive?: boolean;
    showButtonLabel?: boolean;
    onHitDetected?: (hit: IHitMatrixData) => unknown;
    onButtonPressDown?: (index: number, handedness: XRHandedness, self: Group) => unknown;
    onButtonPressUp?: (index: number, handedness: XRHandedness, self: Group) => unknown;
    onXAxisChange?: (value: number, delta: number) => unknown;
    onYAxisChange?: (value: number, delta: number) => unknown;
}

const Controller: FunctionComponent<IParentProps & ThreeElements['group']> = forwardRef(({ 
  space, showRay, handedness, isPlacementMode, onHitDetected, onButtonPressDown, onButtonPressUp, onXAxisChange, onYAxisChange, intersectionDetected, selectActive, hitTestingActive, showButtonLabel, transformActive, children, ...props 
}, ref) => {
  const {scene: threeScene, raycaster, gl} = useThree();
  const controllerGroupRef = useRef<Group>(null!);
  const controllerModelRef = useRef<Group>(null!);
  const geometry = new BufferGeometry();
  const buttonIsPressed = useRef<{[index: string]: boolean}>({});
  const axisValue = useRef<{[index: string]: number}>({[2]: 0, [3]: 0});
  const resetButtonGroupRef = useRef<Group>(null);
  const xrHitTestSourceRef = useRef<XRHitTestSource>(undefined);
  const isXRHitTestSourceSetRef = useRef(false);
  const localReferenceSpaceRef = useRef<XRReferenceSpace | XRBoundedReferenceSpace>(undefined);
  const isLocalRefeneceSpaceSetRef = useRef(false);
  const xrInputSourceRef = useRef<XRInputSource>(undefined);
  const [hasInputSource, setHasInputSource] = useState(false);
  const isPico = Jute.isPico;
  const groundPlaneRef = useRef<Object3D<Object3DEventMap> | undefined>(undefined);

  const isPico4 = navigator.userAgent.includes('PICO 4');

  geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0, 0, 0, -1], 3));
  geometry.setAttribute('color', new Float32BufferAttribute([0.5, 0.5, 0.5, 0, 0, 0], 3));

  useEffect(() => {
    if (!intersectionDetected) {
      geometry.setAttribute('color', new Float32BufferAttribute([0.5, 0.5, 0.5, 0, 0, 0], 3));
      return;
    }
    geometry.setAttribute('color', new Float32BufferAttribute([1.0, 0.5, 0.0, 0, 0, 0], 3));
  }, [intersectionDetected])

  useEffect(() => {
    if (!xrInputSourceRef.current) return;
    const handedness =  xrInputSourceRef.current.handedness;
    const controllerModel = new XRControllerModel({profiles: isPico4 ? ['pico-4'] : xrInputSourceRef.current.profiles, handedness, resetButtonIndex: 5});
    controllerModel.onResetButtonMeshAvailable(mesh => resetButtonGroupRef.current?.position.copy(mesh.position))
    controllerModelRef.current.add(controllerModel);
  }, [hasInputSource])


  // hit testing setup
  useFrame((_state, _delta, xrFrame) => {
    const { session } = xrFrame as XRFrame ?? {};

    if (!xrInputSourceRef.current && session) {
      const inputSources = session.inputSources;
      xrInputSourceRef.current = new Array(...inputSources).filter(source => source.handedness === handedness)[0];
      setHasInputSource(true);
    }

    if (!xrFrame || !isPlacementMode) return;

    if (!hitTestingActive) return;

    if (isPico) {
      if (!groundPlaneRef.current) groundPlaneRef.current = threeScene.getObjectByName('ground-plane');
      if (!groundPlaneRef.current || !controllerGroupRef.current) return;
    
      const hitData = getGroundPlaneHitData(groundPlaneRef.current, raycaster, controllerGroupRef.current);
      onHitDetected?.(hitData);
      return;
    };
 
   
    if (!isLocalRefeneceSpaceSetRef.current) {
      isLocalRefeneceSpaceSetRef.current = true;
      setLocalReferenceSpace(localReferenceSpaceRef, session)
    }

    if (!isXRHitTestSourceSetRef.current && !isPico) {
      isXRHitTestSourceSetRef.current = true;
      setHitTestSource(xrHitTestSourceRef, xrInputSourceRef.current!, session);
    }

    // setHitTestSource is async so xrHittestSource may not be available yet
    if (!xrHitTestSourceRef.current || !localReferenceSpaceRef.current) return;

    const hitData = getHitData(xrFrame, xrHitTestSourceRef.current, localReferenceSpaceRef.current);
    onHitDetected?.(!hitData.detected ? {detected: false} : {detected: true, hitMatrixArray: hitData.xrPose.transform.matrix})
  })

  useFrame((state, _delta, xrFrame) => {
    const frame = xrFrame as XRFrame | undefined;

    if (!frame || !xrInputSourceRef.current) return;
    const buttons = xrInputSourceRef.current.gamepad?.buttons;
    const axes = xrInputSourceRef.current.gamepad?.axes;
    const _space = xrInputSourceRef.current[space === 'grip' ? 'gripSpace' : 'targetRaySpace'];
    const _gripSpace = xrInputSourceRef.current.gripSpace;

    if (buttons) {
      buttons.forEach((button, index) => {
        if (!buttonIsPressed.current[index] && button.pressed) {
          onButtonPressDown?.(index, handedness, controllerGroupRef.current);
        } 
        if (buttonIsPressed.current[index] && !button.pressed) {
          onButtonPressUp?.(index, handedness, controllerGroupRef.current);
        }
        buttonIsPressed.current[index] = button.pressed;
      })
    }

    if (axes) {
      const primaryThumbstickXValue = axes[2];
      const primaryThumbstickYValue = axes[3];

      if ((primaryThumbstickXValue !== axisValue.current[2])) {
        onXAxisChange?.(primaryThumbstickXValue, primaryThumbstickXValue - axisValue.current[2])
      }

      if ((primaryThumbstickYValue !== axisValue.current[3])) {
        onYAxisChange?.(primaryThumbstickYValue, primaryThumbstickYValue - axisValue.current[3])
      } 
    }

    const referenceSpace = state.gl.xr.getReferenceSpace();
    if (!_space || !referenceSpace) return;
    
    const pose = frame.getPose(_space, referenceSpace)
    if (!pose) return;
    controllerGroupRef.current.matrix.fromArray(pose.transform.matrix);  

    if (!_gripSpace) return;
    const gripPose = frame.getPose(_gripSpace, referenceSpace)

    if (!gripPose) return;
    controllerModelRef.current.matrix.fromArray(gripPose.transform.matrix); 
  })

  const labelImageUrl = handedness === 'right' ? 
    'https://d1mfzu0xo6h6ih.cloudfront.net/designer/wt-placement-reset-tag-pointing-right-v1.png' :
    'https://d1mfzu0xo6h6ih.cloudfront.net/designer/wt-placement-reset-tag-pointing-left-v1.png';

  const tagPositionX = handedness === 'left' ? 0.023 : !isPico4 ? -0.023 : -0.04;



	return (
    <group {...props}>
      <group ref={mergeRefs([controllerGroupRef, ref])} name={`controller-${space}-${handedness}`} matrixAutoUpdate={false} >
          {children}
          {showRay && <Beam selectActive={selectActive} highLight={intersectionDetected} transformActive={transformActive} />}
      </group>
      <group ref={controllerModelRef} matrixAutoUpdate={false}>
      <group ref={resetButtonGroupRef} visible={!isPlacementMode && showButtonLabel} rotation={[-10, handedness === 'left' ? 0.12 : -0.12, 0]}>
        <Image
            id={`placement-button-label`}
            renderOrder={Number.POSITIVE_INFINITY}
            rotation={[Math.PI / 2, 0, 0]}
            scale={[0.7 / 20, 0.25 / 20, 1]}
            position={[tagPositionX, -0.005, 0]}
            imageUrl={labelImageUrl}
            curvature={0}
            hasAlpha={true}
            castShadow={true}
            receiveShadow={true}
          />
      </group>
      </group>
    </group>
	);
}) as FunctionComponent<IParentProps & ThreeElements['group']>;

Controller.displayName = 'Controller';
export default memo(Controller);
